import { isLocalUserStreaming } from "../components/Twilio/Room/roomFunction";
import reduxStore from "../configureEnvStore";
import { NOTIFICATION_TYPES } from "../constants/DataTrackNotificationConstants";
import { FIREBASE_EVENT_CATEGORY, FIREBASE_LOG_KEY } from "../constants/FirebaseConstants";
import { FLASH_REASON_ID, FLASH_STATE, HANDLE_FLASH_ON_OR_OFF } from "../constants/FlashConstants";
import { CALL_DATA, GLOBAL_MEETING_DATA } from "../constants/PublicVariables";
import { setFlashState } from "../reducers/flash-reducer";
import { sendDataTrack } from "../shared/dataTrackFunction";
import {
    dualStreamEnabled,
    isDeviceTypeAndroidBrowser,
    isDeviceTypeIOSBrowser,
    isFeatureFlagEnabled,
    isiPhoneOnlyDevice,
    isSafariBelow14,
    isSafariBelow18,
} from "../shared/utility";
import { LogFirebase } from "./firestore";
import navigatorService from "./navigatorService";
import { getLocalVideoTrack, getRoom } from "../components/Twilio/Room/roomService";
import { getTracksByType, restartLocalVideoTracks } from "../components/Twilio/twilioCallUtility";
import DetectRTC from "detectrtc";
class FlashService {
    /* retun only local flash is avialble or not */
    isOnlyLocalFlash = () => {
        const isFlagEnabled = isFeatureFlagEnabled("SHOWMEREMOTEFLASH");
        return !isFlagEnabled; /* As we check remote flash enable, we send it's nagative value as function is checking for local flash only */
    };

    /* common function for handle data track for flash */
    /* old name manageFlashSendDataTrack */
    sendDataTrackForFlash = (msg) => {
        setTimeout(() => {
            sendDataTrack(msg);
            LogFirebase(FIREBASE_EVENT_CATEGORY.Flash, FIREBASE_LOG_KEY.SendMessage, msg);
        }, 1000);
    };

    /* return data track message for flash */
    /* old name getDataTrackForFlash */
    generateDataTrackForLocalFlash = (flashStatus) => {
        const msg = {
            reason_id: FLASH_REASON_ID.ENABLE,
            flash_status: flashStatus,
            device_type: GLOBAL_MEETING_DATA.device_type,
            notification_type: NOTIFICATION_TYPES.FLASHLIGHT.FROM_LOCAL,
            identity: CALL_DATA.localIdentity,
        };
        return msg;
    };

    /* Apply flash changes on UI based on flash supported or not in current device  */
    applyFlashChanges = (disabledFlash, flashState, addFirebaseLog = false) => {
        CALL_DATA.disabledFlash = disabledFlash;
        reduxStore.dispatch(setFlashState(flashState));
        if (!this.isOnlyLocalFlash()) {
            const msg = this.generateDataTrackForLocalFlash(flashState);
            this.sendDataTrackForFlash(msg);
            if (addFirebaseLog) {
                LogFirebase(FIREBASE_EVENT_CATEGORY.Flash, FIREBASE_LOG_KEY.SendMessage, msg);
            }
        }
    };

    /* Check if streaming user is guest user & local user with video on */
    isLocalUsersVideoStreamOn = () => {
        const { startLocalVideo } = reduxStore.getState().twilioEvents;
        return GLOBAL_MEETING_DATA.isGuest && startLocalVideo && isLocalUserStreaming();
    };

    /* check if device is Android mobile and browser */
    isAndroidMobileBrowser = () => {
        return navigatorService.isMobileDevice() && isDeviceTypeAndroidBrowser();
    };

    /* check if device is iOS mobile and browser */
    isIOSMobileBrowser = () => {
        return navigatorService.isMobileDevice() && isDeviceTypeIOSBrowser();
    };

    /* check if device is supported mobile browser (Android or iOS 18+) */
    isSupportedMobileBrowser = () => {
        // Android: always supported
        // iOS: only iOS 18+ supports flashlight
        return this.isAndroidMobileBrowser() || (this.isIOSMobileBrowser() && !isSafariBelow18());
    };

    /* Check if local flash feature is available for the current user */
    checkLocalFlashFeatureAvailable = () => {
        const isFlashSupported = isDeviceTypeAndroidBrowser() || (isDeviceTypeIOSBrowser() && !isSafariBelow18());
        return GLOBAL_MEETING_DATA.isGuest && CALL_DATA.localFlash && isFlashSupported && CALL_DATA.memberId;
    };

    /* Common function to check if we should restart local video tracks for iOS device and Safari
       Returns true only if:
       1. Device is Safari below 14, OR
       2. Device is iPhone AND (flash feature is NOT enabled OR flash is not ON)
       This prevents torch/flashlight flickering when flash is ON */
    shouldRestartLocalVideoTracksForIOSDeviceAndSafari = () => {
        if (isSafariBelow14()) {
            return true;
        }
        if (isiPhoneOnlyDevice()) {
            // If flash feature is enabled and flash is ON, don't restart to prevent flickering
            if (this.checkLocalFlashFeatureAvailable()) {
                const currentFlashState = reduxStore.getState().flash.currentFlashState;
                if (currentFlashState === FLASH_STATE.ON) {
                    return false;
                }
            }
            return true;
        }
        return false;
    };

    /* get flash state based on flash supported or not for updating state and sending data track */
    getFlashStateBasedOnSupport = (flashSupported) => {
        return flashSupported ? FLASH_STATE.ENABLED : FLASH_STATE.DISABLED;
    };

    /* check if device supporting flash or not */
    isFlashSupport = (callback) => {
        const room = getRoom();
        const videoTrack = getTracksByType(room.localParticipant, "video");
        const track = videoTrack?.[0]?.mediaStreamTrack;

        if (!track) {
            LogFirebase(FIREBASE_EVENT_CATEGORY.Flash, FIREBASE_LOG_KEY.FlashSupportError, "No video track available");
            callback(false);
            return;
        }

        // Log device information for debugging
        const deviceType = GLOBAL_MEETING_DATA.device_type;
        const osVersion = DetectRTC.osVersion;
        const browserVersion = DetectRTC.browser.version;

        // Method 1: Use MediaStreamTrack.getCapabilities() - Works on iOS Safari 18+ and Android
        try {
            const capabilities = track.getCapabilities();

            if (capabilities && "torch" in capabilities) {
                const torchSupported = capabilities.torch === true;

                if (torchSupported) {
                    console.log("✅ Torch supported via getCapabilities()");
                    LogFirebase(FIREBASE_EVENT_CATEGORY.Flash, "TorchSupport", {
                        method: "getCapabilities",
                        supported: true,
                        osVersion,
                        browserVersion,
                        deviceType,
                    });
                    callback(true);
                    return;
                } else {
                    LogFirebase(FIREBASE_EVENT_CATEGORY.Flash, "TorchSupport", {
                        method: "getCapabilities",
                        supported: false,
                        osVersion,
                        browserVersion,
                    });
                }
            } else {
                LogFirebase(FIREBASE_EVENT_CATEGORY.Flash, "TorchSupport", {
                    method: "getCapabilities",
                    torchInCapabilities: false,
                    osVersion,
                    browserVersion,
                });
            }
        } catch (error) {
            console.error("❌ getCapabilities() failed:", error.message);
            LogFirebase(FIREBASE_EVENT_CATEGORY.Flash, FIREBASE_LOG_KEY.FlashSupportError, {
                method: "getCapabilities",
                error: error.message,
                osVersion,
                browserVersion,
            });
        }

        // Method 2: Fallback to ImageCapture API for Android Chrome and other browsers
        if (videoTrack && navigatorService.isImageCaptureSupported(videoTrack)) {
            /*
                - if user in chrome browser and imageCapture API is supported for the browser
                then in this condition func will call (ex. windows chrome, edge)
                - this solution for, while screen share visibility issue blank canvas is created for guest user
                - this solution for video and screen share both
            */
            const imageCapture = new ImageCapture(track);
            if (imageCapture) {
                imageCapture.getPhotoCapabilities().then(
                    (capabilities) => {
                        const flashSupported = capabilities?.fillLightMode?.length > 0 && capabilities?.fillLightMode?.includes("flash");

                        if (flashSupported) {
                            LogFirebase(FIREBASE_EVENT_CATEGORY.Flash, "FlashSupport", {
                                method: "ImageCapture",
                                supported: true,
                                fillLightMode: capabilities.fillLightMode,
                                osVersion,
                                browserVersion,
                            });
                        } else {
                            LogFirebase(FIREBASE_EVENT_CATEGORY.Flash, "FlashSupport", {
                                method: "ImageCapture",
                                supported: false,
                                fillLightMode: capabilities?.fillLightMode,
                                osVersion,
                                browserVersion,
                            });
                        }

                        callback(flashSupported);
                    },
                    (error) => {
                        console.error("❌ ImageCapture getPhotoCapabilities() failed:", error.message);
                        LogFirebase(FIREBASE_EVENT_CATEGORY.Flash, FIREBASE_LOG_KEY.FlashSupportError, {
                            method: "ImageCapture",
                            error: error.message,
                            osVersion,
                            browserVersion,
                        });
                        callback(false);
                    }
                );
            } else {
                console.error("❌ ImageCapture instance not created");
                LogFirebase(FIREBASE_EVENT_CATEGORY.Flash, FIREBASE_LOG_KEY.FlashSupportError, {
                    method: "ImageCapture",
                    error: "ImageCapture instance not created",
                    osVersion,
                    browserVersion,
                });
                callback(false);
            }
        } else {
            // No supported method found
            console.error("❌ No flash detection method available");
            LogFirebase(FIREBASE_EVENT_CATEGORY.Flash, FIREBASE_LOG_KEY.FlashSupportError, {
                error: "No supported flash detection method",
                imageCaptureSupported: false,
                osVersion,
                browserVersion,
                deviceType,
            });
            callback(false);
        }
    };

    /* handle and send data track for flashlight */
    /* old name  $rootScope.handleFlashTrack = function (fromFun)  => fromFun = "handleFlashOnOff" */
    handleFlashTrack = (fromFun) => {
        if (this.isLocalUsersVideoStreamOn()) {
            if (this.isSupportedMobileBrowser()) {
                this.isFlashSupport((flashSupported) => {
                    if (flashSupported) {
                        const flashState = fromFun === HANDLE_FLASH_ON_OR_OFF ? FLASH_STATE.OFF : FLASH_STATE.ENABLED;
                        /* flash status 2 is flash OFF (FLASH_STATE.OFF)
                           flash status 3 is flash ENABLED (FLASH_STATE.ENABLED)
                        */
                        this.applyFlashChanges(false, flashState);
                    } else {
                        this.applyFlashChanges(true, FLASH_STATE.DISABLED); // flash status 4 is flash DISABLED
                    }
                });
            } else {
                this.applyFlashChanges(true, FLASH_STATE.DISABLED); // flash status 4 is flash DISABLED
            }
        } else {
            CALL_DATA.disabledFlash = true;
            reduxStore.dispatch(setFlashState(FLASH_STATE.DISABLED));
        }
    };

    disableFlashByStateAndReasonId = (flashStatus, msg) => {
        return (
            flashStatus == FLASH_STATE.DISABLED ||
            msg.reason_id == FLASH_REASON_ID.FRONT_CAMERA_ON ||
            msg.reason_id == FLASH_REASON_ID.APP_IS_IN_BACKGROUND
        ); /* we are getting 1 & 2 value from Mobile device where 1 is for Front camera On & 2 is for app is in background => in this case we will disable flash button */
    };

    /* Apply flash status value based on data track received for local user for flash */
    flashStatusReceiveForAgent = (msg) => {
        reduxStore.dispatch(setFlashState(msg.flash_status));
        const flashStatus = +msg.flash_status;
        CALL_DATA.disabledFlash = flashStatus === FLASH_STATE.ENABLED ? false : this.disableFlashByStateAndReasonId(flashStatus, msg);
    };

    applyMediaStreamTrack = (mediaStreamTrack, applyTorch, callback) => {
        mediaStreamTrack.applyConstraints({
            advanced: [{ torch: applyTorch }],
        });

        callback?.();
    };

    /* Turn flash on/off using media stream track */
    handleFlashOnOff = () => {
        const startLocalVideo = reduxStore.getState().twilioEvents.startLocalVideo;
        if (startLocalVideo && isLocalUserStreaming() && navigatorService.isMobileDevice() && this.isSupportedMobileBrowser()) {
            const localVideoTrack = getLocalVideoTrack();
            const mediaStreamTrack = localVideoTrack?.mediaStreamTrack;
            const currentFlashState = reduxStore.getState().flash.currentFlashState;

            if (!mediaStreamTrack) {
                // No media stream track available, restart tracks
                restartLocalVideoTracks().then(() => {
                    this.handleFlashTrack(HANDLE_FLASH_ON_OR_OFF);
                });
                return;
            }

            if (currentFlashState === FLASH_STATE.OFF || currentFlashState === FLASH_STATE.ENABLED) {
                // Turn flash ON
                CALL_DATA.disabledFlash = false;
                this.applyMediaStreamTrack(mediaStreamTrack, true, () => {
                    reduxStore.dispatch(setFlashState(FLASH_STATE.ON));
                    if (!this.isOnlyLocalFlash()) {
                        const msg = this.generateDataTrackForLocalFlash(FLASH_STATE.ON);
                        sendDataTrack(msg);
                        LogFirebase(FIREBASE_EVENT_CATEGORY.Flash, FIREBASE_LOG_KEY.SendMessage, msg);
                    }
                });
            } else if (currentFlashState === FLASH_STATE.ON) {
                // Turn flash OFF
                CALL_DATA.disabledFlash = false;
                this.applyMediaStreamTrack(mediaStreamTrack, false, () => {
                    reduxStore.dispatch(setFlashState(FLASH_STATE.OFF));
                    if (!this.isOnlyLocalFlash()) {
                        const msg = this.generateDataTrackForLocalFlash(FLASH_STATE.OFF);
                        sendDataTrack(msg);
                        LogFirebase(FIREBASE_EVENT_CATEGORY.Flash, FIREBASE_LOG_KEY.SendMessage, msg);
                    }
                });
            }
        }
    };

    /* Function will be called when received data track for flash for local */
    handleFlashDataTrackForLocal = (msg) => {
        if (GLOBAL_MEETING_DATA.isAgent) {
            LogFirebase(FIREBASE_EVENT_CATEGORY.Flash, FIREBASE_LOG_KEY.ReceivedMessage, msg);
            if (dualStreamEnabled()) {
                if (reduxStore.getState().commonReducer.expandView && msg.identity === reduxStore.getState().twilioEvents.streamingPartiIdentity) {
                    /* for dual stream expand view and general functionality */
                    this.flashStatusReceiveForAgent(msg);
                }
            } else {
                this.flashStatusReceiveForAgent(msg);
            }
            LogFirebase(FIREBASE_EVENT_CATEGORY.Flash, FIREBASE_LOG_KEY.DisabledStatus, CALL_DATA.disabledFlash);
        }
    };

    /* Function will be called when received data track for flash for remote */
    handleFlashDataTrackForRemote = (msg) => {
        LogFirebase(FIREBASE_EVENT_CATEGORY.Flash, FIREBASE_LOG_KEY.ReceivedMessage, msg);
        this.handleFlashOnOff();
        LogFirebase(FIREBASE_EVENT_CATEGORY.Flash, FIREBASE_LOG_KEY.DisabledStatus, CALL_DATA.disabledFlash);
    };
}

export default new FlashService();
